import {computed, inject, Injectable, signal} from '@angular/core';
import * as _ from 'lodash-es';
import {AsyncSubject, Observable, Subject} from 'rxjs';
import {filter, map, startWith, tap} from 'rxjs/operators';
import {AppConfigService} from '../../../../services/config.service';
import type {WebSocketMessage} from '../../../streaming/streaming-interfaces';
import {SubscriptionSubjectType} from '../../../streaming/subscription-subject-type';
import {DelClassificationBaseValueDeliverable} from '../types/del-classifcation';
import {Deliverable, DeliverableId} from '../types/deliverable';
import {Tradable, TradableId} from '../types/tradable';
import {DexStreamingSubjectService} from './dex-subjects-injection';

enum DexStreamingSecuritiesMsgType {
    DELIVERABLE_SNAPSHOT = 'DELIVERABLE_SNAPSHOT',
    DELIVERABLE_UPDATE   = 'DELIVERABLE_UPDATE',
    TRADEABLE_SNAPSHOT   = 'TRADEABLE_SNAPSHOT',
    TRADEABLE_UPDATE     = 'TRADEABLE_UPDATE'
}

interface DexStreamingDeliverablesMsg {
    messageType: DexStreamingSecuritiesMsgType.DELIVERABLE_SNAPSHOT | DexStreamingSecuritiesMsgType.DELIVERABLE_UPDATE;
    data: Deliverable[];
}

interface DexStreamingTradablesMsg {
    messageType: DexStreamingSecuritiesMsgType.TRADEABLE_SNAPSHOT | DexStreamingSecuritiesMsgType.TRADEABLE_UPDATE;
    data: Tradable[];
}

type SubjectMsg = DexStreamingDeliverablesMsg | DexStreamingTradablesMsg;

@Injectable()
export class DexSecuritiesSubjectService implements DexStreamingSubjectService<SubjectMsg> {

    public readonly type: SubscriptionSubjectType<SubjectMsg>;

    public readonly deliverablesUpdated$: Observable<void>;
    public readonly $deliverablesUpdated = signal<void>(undefined, {equal: () => false});
    public readonly tradablesUpdated$: Observable<void>;
    public readonly $tradablesUpdated = signal<void>(undefined, {equal: () => false});

    public readonly $nativeDel = computed(() => {
        this.$deliverablesUpdated();
        const nativeCurrency = this.appConfigService.appConfig.dex.chain.nativeCurrency.symbol;

        return this.getDeliverableBySymbol(nativeCurrency);
    });

    public readonly deliverableMapById: ReadonlyMap<DeliverableId, Deliverable>;
    public readonly deliverableMapBySymbol: ReadonlyMap<string, Deliverable>;
    public readonly tradableMapById: ReadonlyMap<TradableId, Tradable>;

    public readonly deliverablesSnapshotReceived$: Observable<void>;
    public readonly tradablesSnapshotReceived$: Observable<void>;

    private readonly wDeliverableMapById = new Map<DeliverableId, Deliverable>();
    private readonly wDeliverableMapBySymbol = new Map<string, Deliverable>();
    private readonly wTradableMapById = new Map<TradableId, Tradable>();

    private subscriptionSentSubject = new Subject<any>();
    private disconnectedSubject = new Subject<any>();

    private readonly deliverablesUpdatedSubject$ = new Subject<void>();
    private readonly tradablesUpdatedSubject$ = new Subject<void>();

    private readonly deliverablesSnapshotReceivedSubj$ = new AsyncSubject<void>();
    private readonly tradablesSnapshotReceivedSubj$ = new AsyncSubject<void>();

    private readonly appConfigService = inject(AppConfigService);

    constructor() {
        this.type = this.createType();
        this.deliverablesUpdated$ = this.deliverablesUpdatedSubject$.asObservable();
        this.tradablesUpdated$ = this.tradablesUpdatedSubject$.asObservable();

        this.deliverableMapById = this.wDeliverableMapById;
        this.deliverableMapBySymbol = this.wDeliverableMapBySymbol;
        this.tradableMapById = this.wTradableMapById;

        this.deliverablesSnapshotReceived$ = this.deliverablesSnapshotReceivedSubj$.asObservable();
        this.tradablesSnapshotReceived$ = this.tradablesSnapshotReceivedSubj$.asObservable();
    }

    public getDeliverableById(id: DeliverableId): Deliverable {
        return this.wDeliverableMapById.get(id);
    }

    public getTradableById(id: TradableId): Tradable {
        return this.wTradableMapById.get(id);
    }

    public getDeliverableStreamBySymbol(symbol: string): Observable<Deliverable> {
        return this.deliverablesUpdated$.pipe(
            startWith(undefined),
            map(() => this.getDeliverableBySymbol(symbol))
        );
    }

    public getDeliverableBySymbol(symbol: string): Deliverable {
        return this.deliverableMapBySymbol.get(symbol);
    }

    public getDelDisplaySymbol(d: Deliverable): string {
        let resultSymbol: string = d.symbol;
        const baseDelInfo = (d.classification as DelClassificationBaseValueDeliverable).base_value_deliverable;

        if (baseDelInfo) {
            const baseDelId = Number(baseDelInfo.base_value_deliverable_id);
            const baseDel = this.getDeliverableById(baseDelId);

            resultSymbol = baseDel?.symbol || resultSymbol;
        }

        return resultSymbol;
    }

    private createType(): SubscriptionSubjectType<SubjectMsg> {
        return {
            name: 'DEX_SECURITY',
            operations: {
                SUBSCRIBE: 'SUBSCRIBE_SECURITY',
                UNSUBSCRIBE: 'UNSUBSCRIBE_SECURITY'
            },
            messagePipe: _.flow(
                filter((message: WebSocketMessage) => message.messageType in DexStreamingSecuritiesMsgType),
                tap((message: SubjectMsg): void => this.readMessage(message))
            ),
            subscriptionSent$: this.subscriptionSentSubject,
            disconnected$: this.disconnectedSubject,
            deprecateSubject: () => this.deprecateSubject()
        };
    }

    private readMessage(message: SubjectMsg): void {
        switch (message.messageType) {
            case DexStreamingSecuritiesMsgType.DELIVERABLE_SNAPSHOT:
            case DexStreamingSecuritiesMsgType.DELIVERABLE_UPDATE:
                message.data.forEach(item => {
                    this.wDeliverableMapById.set(item.deliverableId, item);
                    this.wDeliverableMapBySymbol.set(item.symbol, item);
                });
                this.deliverablesUpdatedSubject$.next();
                this.$deliverablesUpdated.set();
                break;
            case DexStreamingSecuritiesMsgType.TRADEABLE_SNAPSHOT:
            case DexStreamingSecuritiesMsgType.TRADEABLE_UPDATE:
                message.data.forEach(item =>
                    this.wTradableMapById.set(item.entityId, item)
                );
                this.tradablesUpdatedSubject$.next();
                this.$tradablesUpdated.set();
                break;
        }
        switch (message.messageType) {
            case DexStreamingSecuritiesMsgType.DELIVERABLE_SNAPSHOT:
                this.deliverablesSnapshotReceivedSubj$.next();
                this.deliverablesSnapshotReceivedSubj$.complete();
                break;
            case DexStreamingSecuritiesMsgType.TRADEABLE_SNAPSHOT:
                this.tradablesSnapshotReceivedSubj$.next();
                this.tradablesSnapshotReceivedSubj$.complete();
                break;
        }
    }

    private deprecateSubject(): void {
        this.wDeliverableMapById.clear();
        this.wTradableMapById.clear();
    }

}
